// Packages
import React from "react"
import { graphql } from "gatsby"
import styled, { css } from "styled-components"
import ReactPlayer from "react-player"

// Components
import Header from "@components/global/default-header"
import ABCaresBanner from "@components/global/abcares-banner"
import Stamp from "@images/about/25-year-stamp.svg"
import Leadership from "@components/about/leadership"
import Icon from "@components/global/icon"
import ExternalLinkButton from "@components/global/button-external-link"
import SEO from "@components/global/seo"
import ClientSideOnly from "@components/global/client-side-only"

const size = {
  tablet: 950,
  mobile: 600,
}
const below = Object.keys(size).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (max-width: ${size[label]}px) {
      ${css(...args)};
    }
  `
  return acc
}, {})

const above = Object.keys(size).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (min-width: ${size[label]}px) {
      ${css(...args)};
    }
  `
  return acc
}, {})

const Wrapper = styled.div`
  overflow: hidden;
  /* .background {
    img {
      ${below.mobile`min-width: 140%; transform: translateX(-29%);`}
    }
  } */
  h2 {
    margin-bottom: 32px;
    ${below.tablet`
    text-align: left;

    margin-bottom: 16px;
    `}
    ${below.mobile`
    margin-bottom: 8px; 
    `}
  }
`
const Container = styled.div`
  max-width: 1248px;
  margin: 0 auto;
  padding: 88px 48px;
  ${below.tablet`padding: 48px 48px;`}
  ${below.mobile`
    padding: 48px 24px 64px;
    `}
`
const OurStoryContainer = styled.div`
  position: relative;
  margin: 0 auto;
  max-width: 680px;
  ${below.tablet`
  margin: 0;
  max-width: 498px;
  `}

  h2 {
    text-align: center;
  }
  img {
    position: absolute;
    bottom: -30%;
    right: -185px;
    ${below.tablet`
    width: 202px;
    top: -10%;
    right: -130px;
    `}
    ${below.mobile`
    width: 96px;
    right: -25px;
    top: -25px;`}
  }
`
const PhilosophyWrapper = styled.div`
  background-color: #042943;

  .container {
    position: relative;
    max-width: 1248px;
    margin: 0 auto;
    padding: 72px 48px 96px;
    display: flex;
    align-items: center;
    ${below.tablet`padding: 72px 48px 88px; flex-direction: column; align-items: flex-start;`}
    ${below.mobile`
    padding: 0 24px 112px;
    `}
  }
  h2 {
    color: white;
    text-align: left;
    margin-bottom: 48px;
    ${below.tablet`margin-bottom: 32px;`}
    ${below.mobile`margin-bottom: 40px;`}
  }
`
const IconWrapper = styled.div`
  border-radius: 8px;
  background: #031d30;
  box-shadow: 0px 2px 24px rgba(0, 0, 0, 0.1);
  width: 88px;
  height: 88px;
  min-width: 88px;
  min-height: 88px;
  padding: 20px;
  margin-right: 24px;
  ${below.mobile`width: 64px; height: 64px; min-width: 64px; min-height: 64px;`}
`
const Values = styled.div`
  flex: 1;
  margin-right: 48px;
  ${below.tablet`margin-right: 0px;`}
  .flex-container {
    display: flex;
    align-items: center;
    margin-bottom: 48px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  .value-title {
    text-transform: uppercase;
    color: white;
  }
  .value-description {
    color: white;
    margin-bottom: 0;
  }
`
const VideoWrapper = styled.div`
  flex: 1;
  ${below.tablet`order: -1; min-width: 100%; margin: 0 auto 72px; padding: 0 40px; `}
  ${below.mobile` min-width: 100vw; margin: 0 0 48px -24px; padding: 0;`}
`
const PlayerWrapper = styled.div`
  position: relative;
  padding-top: 56.25%;
`

const WereHiringPeopleJustLikeYou = styled.div`
  background: var(--ice-grey);
  padding: 48px 24px;
  ${above.mobile`
padding:48px;
`}
`

const WHPJLYContainer = styled.div`
  ${above.mobile`
display:flex;
justify-content: space-between;
align-items:center;
`}

  ${above.tablet`
max-width:796px;
margin: 0 auto;
`}

  h3 {
    font-weight: 600;
    color: var(--grey);
    margin-bottom: 48px;

    ${above.mobile`
    margin-bottom:0;
    margin-right:48px;
    `}
    @media screen and (max-width: 600px) {
      font-size: 36px;
      line-height: 44px;
    }
  }

  .external-button {
    max-width: 328px;
    max-height: 44px;
    ${above.mobile`
    min-width:270px;
    `}

    ${above.mobile`
    width:268px;
    `}
  }
`

const AboutPage = props => {
  return (
    <>
      <Wrapper>
        <Header
          customHero={props.data.Hero.childImageSharp.gatsbyImageData}
          headline="This is Atlantic Bay."
          caption="National Sales Manager, Emily Farley Gardner, and Chompers enjoy bring your dog to work day."
          objectPosition="top center"
          NMNLogo
        />
        <Container>
          <OurStoryContainer>
            <h2>Our story.</h2>
            <p>
              Since 1996, we’ve been serving our communities with great loan
              products, customer service, and expertise. We’re a trusted lender
              and have stayed true to our core values throughout our more than
              25 years. We’ll continue to hold our relationships, culture,
              commitment to the community, and credibility as our highest
              mission.
            </p>
            <img src={Stamp} alt="25 years of service" />
          </OurStoryContainer>
        </Container>
        <PhilosophyWrapper>
          <div className="container">
            <Values>
              <h2 className="fancy-type regular-weight">What we believe.</h2>
              <div className="flex-container">
                <IconWrapper>
                  <Icon icon="core-care" />
                </IconWrapper>

                <div className="value-wrapper">
                  <div className="value-title overline-lrg">
                    We genuinely care
                  </div>
                  <p className="value-description">
                    It’s simple. We put people first.
                  </p>
                </div>
              </div>
              <div className="flex-container">
                <IconWrapper>
                  <Icon icon="core-growth" />
                </IconWrapper>

                <div className="value-wrapper">
                  <div className="value-title overline-lrg">
                    We inspire growth
                  </div>
                  <p className="value-description">
                    Adapt and improve with passionate positivity.
                  </p>
                </div>
              </div>
              <div className="flex-container">
                <IconWrapper>
                  <Icon icon="core-fun" />
                </IconWrapper>

                <div className="value-wrapper">
                  <div className="value-title overline-lrg">We have fun</div>
                  <p className="value-description">
                    We love what we do while making a difference.
                  </p>
                </div>
              </div>
            </Values>
            <ClientSideOnly>
              <VideoWrapper>
                <PlayerWrapper>
                  <ReactPlayer
                    url="https://home.wistia.com/medias/lxvsqjlzw4"
                    className="react-player"
                    width="100%"
                    height="100%"
                    config={{
                      wistia: {
                        options: {
                          playerColor: "0274E8",
                          doNotTrack: true,
                          fullscreenButton: true,
                        },
                      },
                    }}
                  />
                </PlayerWrapper>
              </VideoWrapper>
            </ClientSideOnly>
          </div>
        </PhilosophyWrapper>
        <WereHiringPeopleJustLikeYou>
          <WHPJLYContainer>
            <h3 className="sans">We're hiring people just like you.</h3>
            <ExternalLinkButton
              className="external-button"
              linkTo="https://gusea1p01.rec.pro.ukg.net/ATL1013ABMG"
              buttonText="Explore open opportunities"
            />
          </WHPJLYContainer>
        </WereHiringPeopleJustLikeYou>
        <Leadership />
        <ABCaresBanner
          walledSlug={props.bankerData?.slug}
          walledVersion={props.version}
          isMounted={props.isMounted}
        />
      </Wrapper>
    </>
  )
}

// Meta tags
export const Head = props => {
  const canonicalSlug = props.pageContext.canonicalSlug
  return (
    <SEO
      description={
        "Since 1996, we’ve been serving our communities with great loan products, customer service, and expertise. We continue to hold our relationships, culture, commitment to the community, and credibility as our highest mission."
      }
      ogImage={
        "https://images.ctfassets.net/n1no4gieqp7s/5SDikyR6bnvESO2Er0Gz6z/d163b15133976f20be4ef4348424fe62/og-abcares.jpg"
      }
      title={`Our Story | Atlantic Bay Mortgage Group®`}
      canonicalSlug={canonicalSlug}
      slug={props.location.pathname.replace(/^\//, "").replace(/\/$/, "")}
    />
  )
}

export default AboutPage

export const query = graphql`
  query {
    Hero: file(
      relativePath: { regex: "/about/emily-farley-and-chompers.jpg/" }
    ) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH)
      }
    }
  }
`
